import React, { useState, useEffect } from 'react';
import {
  Typography, Button, Box, Alert, CircularProgress, Autocomplete,
  TextField, Chip, ToggleButtonGroup, ToggleButton, List,
  ListItem, ListItemText, IconButton, Collapse,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ModalWrapper from '../components/ModalWrapper';
import ShoppingListForm from '../forms/ShoppingListForm';
import { ShoppingList, ShoppingListFormData, emptyShoppingListFormData } from '../types/ShoppingList';
import { ShoppingListGrocery } from '../types/ShoppingListGrocery';
import { apiFetch } from '../apiFetch';
import { shouldShowPWAPrompt } from '../utils/pwa';
import PWAPromptModal from './PWAPromptModal';

interface PrefillItem {
  grocery: number;
  quantity: number;
}

interface ShoppingListFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (shoppingList: ShoppingList) => void;
  shoppingList?: ShoppingList | null;
  // Optional prefill props for creating lists from analysis
  prefillName?: string;
  prefillDescription?: string;
  prefillItems?: PrefillItem[];
}

const ShoppingListFormModal: React.FC<ShoppingListFormModalProps> = ({
  open,
  onClose,
  onSuccess,
  shoppingList,
  prefillName,
  prefillDescription,
  prefillItems,
}) => {
  const [formData, setFormData] = useState<ShoppingListFormData>(emptyShoppingListFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof ShoppingListFormData, string>>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Copy from existing list state
  const [existingLists, setExistingLists] = useState<ShoppingList[]>([]);
  const [listsLoading, setListsLoading] = useState(false);
  const [selectedSourceLists, setSelectedSourceLists] = useState<ShoppingList[]>([]);
  const [filterStatus, setFilterStatus] = useState<'all' | 'open' | 'done'>('all');

  // Inline preview state - tracks which list ID is expanded
  const [expandedListId, setExpandedListId] = useState<number | null>(null);
  const [previewItems, setPreviewItems] = useState<ShoppingListGrocery[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Track the merged items count and details for selected lists
  const [mergedItemsCount, setMergedItemsCount] = useState<number>(0);
  const [mergedItemsLoading, setMergedItemsLoading] = useState(false);

  // PWA prompt state for first list creation
  const [pwaPromptOpen, setPwaPromptOpen] = useState(false);
  const [pendingSavedList, setPendingSavedList] = useState<ShoppingList | null>(null);

  const isEditing = !!shoppingList;
  const apiUrl = process.env.REACT_APP_API_URL;

  // Helper function to filter out deleted groceries
  const filterActiveItems = (items: ShoppingListGrocery[]): ShoppingListGrocery[] => {
    return items.filter(item => !item.grocery_details.deleted);
  };

  // Fetch existing lists when modal opens (only for new lists)
  useEffect(() => {
    if (open && !isEditing) {
      const fetchLists = async () => {
        setListsLoading(true);
        try {
          const data = await apiFetch(`${apiUrl}/api/shopping-lists/`);
          // Only include lists with items (item_count now excludes deleted groceries from backend)
          const listsWithItems = data.filter((list: ShoppingList) => list.item_count > 0);
          setExistingLists(listsWithItems);
        } catch (err: any) {
          console.error('Failed to load existing lists:', err);
        } finally {
          setListsLoading(false);
        }
      };
      fetchLists();
    }
  }, [open, isEditing, apiUrl]);

  // Helper function to merge items from multiple lists, keeping highest quantity for duplicates
  const mergeItemsFromLists = (allItems: ShoppingListGrocery[]): Map<number, ShoppingListGrocery> => {
    const mergedMap = new Map<number, ShoppingListGrocery>();
    for (const item of allItems) {
      const existing = mergedMap.get(item.grocery);
      if (!existing || item.quantity > existing.quantity) {
        mergedMap.set(item.grocery, item);
      }
    }
    return mergedMap;
  };

  // Fetch and calculate merged item count when source lists change
  useEffect(() => {
    if (selectedSourceLists.length === 0) {
      setMergedItemsCount(0);
      return;
    }

    const fetchMergedCount = async () => {
      setMergedItemsLoading(true);
      try {
        // Fetch items from all selected lists
        const allItemsPromises = selectedSourceLists.map((list) =>
          apiFetch(`${apiUrl}/api/shopping-lists/${list.id}/groceries/`)
        );
        const allItemsArrays = await Promise.all(allItemsPromises);

        // Flatten and filter active items
        const allActiveItems = allItemsArrays.flat().filter(
          (item: ShoppingListGrocery) => !item.grocery_details.deleted
        );

        // Merge items, keeping highest quantity for duplicates
        const mergedMap = mergeItemsFromLists(allActiveItems);
        setMergedItemsCount(mergedMap.size);
      } catch (err) {
        // Fallback to sum of item counts
        const totalCount = selectedSourceLists.reduce((sum, list) => sum + list.item_count, 0);
        setMergedItemsCount(totalCount);
      } finally {
        setMergedItemsLoading(false);
      }
    };

    fetchMergedCount();
  }, [selectedSourceLists, apiUrl]);

  // Populate form when editing or when prefill props are provided
  useEffect(() => {
    if (shoppingList) {
      setFormData({
        name: shoppingList.name || '',
        description: shoppingList.description || '',
      });
    } else if (prefillName || prefillDescription) {
      // Use prefill values if provided
      setFormData({
        name: prefillName || '',
        description: prefillDescription || '',
      });
      setSelectedSourceLists([]);
    } else {
      setFormData(emptyShoppingListFormData);
      setSelectedSourceLists([]);
    }
    setErrors({});
    setApiError(null);
    setExpandedListId(null);
    setPreviewItems([]);
  }, [shoppingList, open, prefillName, prefillDescription]);

  const handleChange = (field: keyof ShoppingListFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof ShoppingListFormData, string>> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'List name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    setApiError(null);

    const payload: Record<string, any> = {
      name: formData.name.trim(),
      description: formData.description.trim() || null,
    };

    try {
      let savedList: ShoppingList;
      if (isEditing && shoppingList) {
        savedList = await apiFetch(`${apiUrl}/api/shopping-lists/${shoppingList.id}/`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        // Create new list
        savedList = await apiFetch(`${apiUrl}/api/shopping-lists/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        // If prefillItems provided, add those items
        if (prefillItems && prefillItems.length > 0) {
          try {
            for (const item of prefillItems) {
              await apiFetch(`${apiUrl}/api/shopping-lists/${savedList.id}/groceries/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  grocery: item.grocery,
                  quantity: item.quantity,
                }),
              });
            }
            // Refetch the saved list to get updated item_count
            savedList = await apiFetch(`${apiUrl}/api/shopping-lists/${savedList.id}/`);
          } catch (copyErr: any) {
            console.error('Failed to add prefill items:', copyErr);
          }
        }
        // If copying from existing lists, copy the items (excluding deleted groceries)
        else if (selectedSourceLists.length > 0) {
          try {
            // Fetch items from all source lists
            const allItemsPromises = selectedSourceLists.map((list) =>
              apiFetch(`${apiUrl}/api/shopping-lists/${list.id}/groceries/`)
            );
            const allItemsArrays = await Promise.all(allItemsPromises);

            // Flatten and filter out deleted groceries
            const allActiveItems = allItemsArrays.flat().filter(
              (item: ShoppingListGrocery) => !item.grocery_details.deleted
            );

            // Merge items, keeping highest quantity for duplicates
            const mergedMap = mergeItemsFromLists(allActiveItems);
            const mergedItems = Array.from(mergedMap.values());

            // Copy each merged item to the new list (without picked values)
            for (const item of mergedItems) {
              await apiFetch(`${apiUrl}/api/shopping-lists/${savedList.id}/groceries/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  grocery: item.grocery,
                  quantity: item.quantity,
                }),
              });
            }

            // Refetch the saved list to get updated item_count
            savedList = await apiFetch(`${apiUrl}/api/shopping-lists/${savedList.id}/`);
          } catch (copyErr: any) {
            console.error('Failed to copy items:', copyErr);
            // Continue anyway - list was created, just items weren't copied
          }
        }
      }
      // Check if this was the first list creation (for PWA prompt)
      if (!isEditing && existingLists.length === 0) {
        try {
          const profileData = await apiFetch(`${apiUrl}/api/account/profile/`);
          if (shouldShowPWAPrompt(profileData.pwa_installed, profileData.pwa_prompt_disabled)) {
            // Show PWA prompt before completing
            setPendingSavedList(savedList);
            setPwaPromptOpen(true);
            return;
          }
        } catch (err) {
          console.error('Error checking PWA status:', err);
        }
      }

      onSuccess(savedList);
      onClose();
    } catch (err: any) {
      setApiError(err.message || 'An error occurred while saving the shopping list.');
    } finally {
      setLoading(false);
    }
  };

  // Handle PWA prompt close (either installed or dismissed)
  const handlePwaPromptClose = () => {
    setPwaPromptOpen(false);
    if (pendingSavedList) {
      onSuccess(pendingSavedList);
      setPendingSavedList(null);
      onClose();
    }
  };

  // Filter lists based on status
  const filteredLists = existingLists.filter((list) => {
    if (filterStatus === 'open') return !list.completed;
    if (filterStatus === 'done') return list.completed;
    return true;
  });

  // Handle inline preview toggle
  const handlePreviewClick = async (event: React.MouseEvent<HTMLElement>, list: ShoppingList) => {
    event.stopPropagation();

    // If already expanded, collapse it
    if (expandedListId === list.id) {
      setExpandedListId(null);
      setPreviewItems([]);
      return;
    }

    setExpandedListId(list.id);
    setPreviewLoading(true);

    try {
      const items: ShoppingListGrocery[] = await apiFetch(`${apiUrl}/api/shopping-lists/${list.id}/groceries/`);
      // Filter out deleted groceries in preview
      const activeItems = filterActiveItems(items);
      setPreviewItems(activeItems);
    } catch (err: any) {
      console.error('Failed to load preview:', err);
      setPreviewItems([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <ModalWrapper open={open} onClose={onClose}>
      <Typography variant="h6" gutterBottom>
        {isEditing ? 'Edit Shopping List' : 'Create New Shopping List'}
      </Typography>

      {apiError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {apiError}
        </Alert>
      )}

      <ShoppingListForm formData={formData} onChange={handleChange} errors={errors} />

      {/* Show prefill items info when provided */}
      {!isEditing && prefillItems && prefillItems.length > 0 && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            This list will be created with {prefillItems.length} item{prefillItems.length !== 1 ? 's' : ''} from the analysis.
          </Typography>
        </Box>
      )}

      {/* Copy from existing list - only show for new lists without prefillItems */}
      {!isEditing && !prefillItems && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Copy items from existing lists (optional)
          </Typography>

          {/* Filter toggles */}
          <ToggleButtonGroup
            value={filterStatus}
            exclusive
            onChange={(_, value) => value && setFilterStatus(value)}
            size="small"
            sx={{ mb: 1 }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="open">Open</ToggleButton>
            <ToggleButton value="done">Done</ToggleButton>
          </ToggleButtonGroup>

          {/* Autocomplete for selecting source lists */}
          <Autocomplete
            multiple
            options={filteredLists}
            getOptionLabel={(option) => option.name}
            value={selectedSourceLists}
            onChange={(_, newValue) => setSelectedSourceLists(newValue)}
            loading={listsLoading}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disableCloseOnSelect
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              const isExpanded = expandedListId === option.id;
              return (
                <Box key={key} component="li" {...otherProps} sx={{ flexDirection: 'column', alignItems: 'stretch !important' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Typography>{option.name}</Typography>
                      <Chip
                        label={option.completed ? 'Done' : 'Open'}
                        size="small"
                        color="primary"
                        variant={option.completed ? 'filled' : 'outlined'}
                        sx={{ fontSize: '0.65rem', height: '18px' }}
                      />
                      <Chip
                        label={`${option.item_count} ${option.item_count === 1 ? 'item' : 'items'}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: '18px' }}
                      />
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => handlePreviewClick(e, option)}
                      sx={{ ml: 1 }}
                    >
                      <VisibilityIcon fontSize="small" color={isExpanded ? 'primary' : 'inherit'} />
                    </IconButton>
                  </Box>
                  <Collapse in={isExpanded} timeout="auto">
                    <Box sx={{ pl: 2, pr: 1, py: 1, bgcolor: 'action.hover', borderRadius: 1, mt: 1 }}>
                      {previewLoading ? (
                        <CircularProgress size={16} />
                      ) : previewItems.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No items</Typography>
                      ) : (
                        <List dense disablePadding>
                          {previewItems.map((item) => (
                            <ListItem key={item.id} disablePadding sx={{ py: 0.25 }}>
                              <ListItemText
                                primary={
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                    {item.quantity}× {item.grocery_details.name}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    label={option.name}
                    size="small"
                    {...tagProps}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selectedSourceLists.length === 0 ? "Search existing lists..." : ""}
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {listsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* Selected lists indicator */}
          {selectedSourceLists.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {mergedItemsLoading ? (
                'Calculating items...'
              ) : (
                <>
                  Will copy {mergedItemsCount} unique {mergedItemsCount === 1 ? 'item' : 'items'} from {selectedSourceLists.length} {selectedSourceLists.length === 1 ? 'list' : 'lists'}
                  {selectedSourceLists.length > 1 && ' (duplicates merged, highest quantity kept)'}
                </>
              )}
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button variant="outlined" color="primary" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : isEditing ? 'Save Changes' : 'Create List'}
        </Button>
      </Box>

      {/* PWA Prompt Modal for first list creation */}
      <PWAPromptModal
        open={pwaPromptOpen}
        onClose={handlePwaPromptClose}
        onInstalled={handlePwaPromptClose}
      />
    </ModalWrapper>
  );
};

export default ShoppingListFormModal;